import { useTranslation } from "react-i18next";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { PMField } from "../../../utils/PMField";
import { ProductSelector } from "../../../utils/Selector/productSelector";
import { CategorySelector } from "../../../utils/Selector/categorySelector";
import { FieldHint } from "./field-hint";
import {
  nestedFieldName,
  nestedRowFieldError,
} from "./agreementNestedFormSchemas";

const KINDS = ["manual", "category", "product"];

function emptyDiscountLine(kind = "manual") {
  return {
    id: null,
    kind,
    label: "",
    discount_percent: "",
    product_id: null,
    product_category_id: null,
    product: null,
    product_category: null,
  };
}

function FieldError({ message }) {
  if (!message) return null;
  return <p className="mt-1 text-xs font-medium text-rose-600">{message}</p>;
}

export function ServiceAgreementDiscountLinesEditor({
  lines = [],
  onChange,
  formik,
}) {
  const { t } = useTranslation();

  const visible = lines
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => !row._destroy);

  const update = (index, patch) => {
    onChange(lines.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const setKind = (index, kind) => {
    update(index, {
      kind,
      product_id: kind === "product" ? lines[index].product_id : null,
      product: kind === "product" ? lines[index].product : null,
      product_category_id:
        kind === "category" ? lines[index].product_category_id : null,
      product_category:
        kind === "category" ? lines[index].product_category : null,
    });
  };

  const remove = (index) => {
    const row = lines[index];
    if (row?.id) {
      onChange(
        lines.map((item, i) => (i === index ? { ...item, _destroy: true } : item))
      );
      return;
    }
    onChange(lines.filter((_, i) => i !== index));
  };

  return (
    <div className="col-span-6 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-slate-500">
          {t("service.agreements.discount_lines.help")}
        </p>
        <FieldHint content={t("service.agreements.discount_lines.hint")} />
      </div>

      {visible.length === 0 ? (
        <p className="text-sm text-slate-400">
          {t("service.agreements.discount_lines.empty")}
        </p>
      ) : null}

      {visible.map(({ row, index }) => (
        <div
          key={row.id || `discount-${index}`}
          className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4"
        >
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex rounded-full bg-white p-1 text-xs font-semibold shadow-sm ring-1 ring-slate-200">
              {KINDS.map((kind) => (
                <button
                  key={kind}
                  type="button"
                  onClick={() => setKind(index, kind)}
                  className={`rounded-full px-3 py-1 ${
                    row.kind === kind
                      ? "bg-[#1463C7] text-white"
                      : "text-slate-600"
                  }`}
                >
                  {t(`service.agreements.discount_lines.kind.${kind}`)}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => remove(index)}
              className="rounded-full p-2 text-rose-600 hover:bg-rose-50"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-6 gap-4">
            {row.kind === "manual" ? (
              <div className="col-span-6 sm:col-span-4">
                <PMField
                  name={nestedFieldName("discount_lines", index, "label")}
                  type="text"
                  label={t("service.agreements.discount_lines.label")}
                  values={{
                    [nestedFieldName("discount_lines", index, "label")]:
                      row.label,
                  }}
                  onChange={(e) => update(index, { label: e.target.value })}
                  onBlur={() =>
                    formik?.setFieldTouched(
                      nestedFieldName("discount_lines", index, "label"),
                      true,
                      false
                    )
                  }
                />
                <FieldError
                  message={nestedRowFieldError(
                    formik,
                    "discount_lines",
                    index,
                    "label"
                  )}
                />
              </div>
            ) : null}

            {row.kind === "category" ? (
              <div className="col-span-6 sm:col-span-4">
                <CategorySelector
                  name={nestedFieldName(
                    "discount_lines",
                    index,
                    "product_category_id"
                  )}
                  label={t("service.agreements.discount_lines.category")}
                  selected={row.product_category}
                  onChange={(category) =>
                    update(index, {
                      product_category_id: category?.id || null,
                      product_category: category
                        ? {
                            id: category.id,
                            title: category.title || category.displayTitle,
                            displayTitle:
                              category.displayTitle || category.title,
                          }
                        : null,
                    })
                  }
                />
                <FieldError
                  message={nestedRowFieldError(
                    formik,
                    "discount_lines",
                    index,
                    "product_category_id"
                  )}
                />
              </div>
            ) : null}

            {row.kind === "product" ? (
              <div className="col-span-6 sm:col-span-4">
                <ProductSelector
                  name={nestedFieldName("discount_lines", index, "product_id")}
                  label={t("service.agreements.discount_lines.product")}
                  selected={row.product}
                  onChange={(product) =>
                    update(index, {
                      product_id: product?.id || null,
                      product: product || null,
                    })
                  }
                />
                <FieldError
                  message={nestedRowFieldError(
                    formik,
                    "discount_lines",
                    index,
                    "product_id"
                  )}
                />
              </div>
            ) : null}

            <div className="col-span-6 sm:col-span-2">
              <PMField
                name={nestedFieldName(
                  "discount_lines",
                  index,
                  "discount_percent"
                )}
                type="number"
                label={t("service.agreements.discount_lines.percent")}
                values={{
                  [nestedFieldName(
                    "discount_lines",
                    index,
                    "discount_percent"
                  )]: row.discount_percent,
                }}
                onChange={(e) =>
                  update(index, { discount_percent: e.target.value })
                }
                onBlur={() =>
                  formik?.setFieldTouched(
                    nestedFieldName(
                      "discount_lines",
                      index,
                      "discount_percent"
                    ),
                    true,
                    false
                  )
                }
              />
              <FieldError
                message={nestedRowFieldError(
                  formik,
                  "discount_lines",
                  index,
                  "discount_percent"
                )}
              />
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() => onChange([...lines, emptyDiscountLine("manual")])}
        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
      >
        <PlusIcon className="h-4 w-4" />
        {t("service.agreements.discount_lines.add")}
      </button>
    </div>
  );
}
